import React, { useState, useEffect } from 'react';
import { fetchSlots } from '../services/slots.service';
import { createAppointmentRecord } from '../services/appointments.service';
import { getSubmissionByMobileNumber,addSubmission,setNeedsAlternateTime } from '../services/submissions.service';
import { sendAppointmentNotification } from '../services/notifications.service';
import { assignRandomVariant } from '../services/userMessageVariant.service';
import moment from 'moment-timezone';
import { useSearchParams } from 'react-router-dom';


const PublicBookingPage = () => {
  const [mobileNumber, setMobileNumber] = useState('');
  const [slots, setSlots] = useState([]);
  const [nextToken, setNextToken] = useState(null);
  const [submissionId, setSubmissionId] = useState(null);
  const [bookingSlotId, setBookingSlotId] = useState(null);
  const [confirmation, setConfirmation] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [timezone, setTimezone] = useState('America/New_York');
  const SLOTS_PER_PAGE = 3;

  const timezoneLabel = timezone === 'America/New_York' ? 'ET' : 'PT';

  const [searchParams] = useSearchParams();
  useEffect(() => {
    const sid = searchParams.get('SID');
    console.log('SID:', sid);
  }, [searchParams]);

  useEffect(() => {
    loadSlots();
  }, []);

  const sortSlots = (slots) => {
    return slots.sort((a, b) => {
      if (a.date < b.date) return -1;
      if (a.date > b.date) return 1;
      if (a.startTime < b.startTime) return -1;
      if (a.startTime > b.startTime) return 1;
      return 0;
    });
  };

  const loadSlots = async () => {
    setLoading(true);
    setSlots([]);
    setNextToken(null);
    setCurrentPage(0);
    let allFutureSlots = [];
    let nextToken = null;
    try {
      do {
        const result = await fetchSlots({}, 50, nextToken);
        if (result && result.items && result.items.length > 0) {
          allFutureSlots = [...allFutureSlots, ...result.items];
        }
        nextToken = result.nextToken;
      } while (nextToken);
      allFutureSlots = sortSlots(allFutureSlots);
      setSlots(allFutureSlots);
      setNextToken(null);
    } catch (err) {
      setError('Failed to load available times. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadMoreSlots = async () => {
    if (!nextToken) return;
    setLoading(true);
    try {
      const result = await fetchSlots({}, 3, nextToken);
      if (result && result.items) {
        const now = new Date();
        const upcoming = result.items.filter(slot => {
          const slotTime = new Date(`${slot.date}T${slot.startTime}`);
          return slotTime > now;
        });
        setSlots(prev => [...prev, ...upcoming]);
        setNextToken(result.nextToken);
      }
    } catch (err) {
      console.error('Failed to load more slots:', err);
      setError('Failed to load more times. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  const isValidUSMobile = (number) => /^\d{10}$/.test(number);

  const handleLookupSubmission = async () => {
    setError('');
    setConfirmation('');
    setSubmissionId(null);
    try {
      const submission = await getSubmissionByMobileNumber(mobileNumber);
      if (submission) {
        setSubmissionId(submission.id);
      } else {
        setError('Submission not found. Please contact support.');
      }
    } catch (err) {
      setError('Error finding submission.');
      console.error(err);
    }
  };

  const handleBook = async (slot) => {
    setLoading(true);
    setError('');
    setConfirmation('');
  
    const SID = searchParams.get('SID');
    if (!SID || !mobileNumber) {
      setError('Scheduling requires a U.S. mobile number. Please try again.');
      setLoading(false); // Important: reset loading before exiting
      return; // 🔴 Exit early to stop execution
    }   
    if (!isValidUSMobile(mobileNumber)) {
      setError('Scheduling requires a U.S. mobile number. Please try again.');
      setLoading(false); // Important: reset loading before exiting
      return; // 🔴 Exit early to stop execution
    } 

    const result = await addSubmission({ mobileNumber, SID});
    const submissionId = result.id;
    try {
      await createAppointmentRecord({
        submissionId,
        slotId: slot.id,
        status: 'SCHEDULED',
        bookedAt: new Date().toISOString(),
      });

      // Assign random variant track (creates or updates single entry per user)
      let variantTrack = 1; // default fallback
      try {
        const variantAssignment = await assignRandomVariant(submissionId);
        variantTrack = variantAssignment?.variantTrack || 1;
        console.log(`[INFO] Assigned random variant track ${variantTrack} for appointment`);
      } catch (variantError) {
        console.error('[WARN] Failed to assign variant, using default track 1:', variantError);
      }
  
      await sendAppointmentNotification({
        mobileNumber: mobileNumber,
        slotDate: slot.date,
        startTime: slot.startTime,
        variantTrack: variantTrack,
        submissionId: submissionId
      });
  
      await loadSlots();
      setBookingSlotId(slot.id);
      setConfirmation(
        <>
          Thank you for scheduling your virtual call!
          <br />
          You'll receive a confirmation and call link via text.
        </>
      );
      setIsSubmitted(true);

      // Attempt to auto-close
      setTimeout(() => {
        window.parent.postMessage({ closeIframe: true }, '*');
      }, 5000);
    } catch (err) {
      setError('Failed to book appointment.');
      console.error(err);
    }
  
    setLoading(false);
  };
  
  const handlePreviousPage = () => {
    setCurrentPage(prev => Math.max(0, prev - 1));
  };

  const handleNextPage = () => {
    setCurrentPage(prev => Math.min(Math.ceil(slots.length / SLOTS_PER_PAGE) - 1, prev + 1));
  };

  const handleRequestDifferentTime = async () => {
    setLoading(true);
    setError('');
    setConfirmation('');

    const SID = searchParams.get('SID');
    if (!SID || !mobileNumber) {
      setError('Please enter your phone number first.');
      setLoading(false);
      return;
    }
    if (!isValidUSMobile(mobileNumber)) {
      setError('Please enter a valid U.S. mobile number.');
      setLoading(false);
      return;
    }

    try {
      const result = await addSubmission({ mobileNumber, SID });

      // Flag the submission so staff can quickly see this user needs alternate times
      if (result?.id) {
        try {
          await setNeedsAlternateTime(result.id, true, 'Public Booking');
        } catch (flagErr) {
          console.error('Failed to flag submission for alternate time:', flagErr);
        }
      }

      setConfirmation('Your request has been sent. Our team will reach out with more options.');
      setIsSubmitted(true);

      setTimeout(() => {
        window.parent.postMessage({ closeIframe: true }, '*');
      }, 5000);
    } catch (err) {
      console.error('Failed to request different time:', err);
      setError('Failed to send request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 z-50">
      <div className="w-full max-w-sm bg-white rounded-xl p-6 relative shadow-lg border-2 border-[#003366]"      >
        <div className="flex flex-col items-center text-center">
        <img src="/columbia-irving-logo.png" alt="Columbia University" className="w-auto h-24 sm:h-28 md:h-32" />
        <p className="text-gray-700 mt-2 mb-2 font-medium">
          Enter your phone number & select a time 
          for a virtual chat with our support team.
        </p>
          <p className="text-sm text-gray-500 mb-4">
            (We'll text you the confirmation and the call link.)
          </p>
  
          <input
            type="tel"
            value={mobileNumber.replace('+1', '')}
            onChange={(e) => {
              setMobileNumber(e.target.value.replace(/\D/g, ''));
              setError(''); // 👈 clear error when editing input
            }}
            placeholder="1234567890"
            className="w-full px-4 py-2 border rounded-md text-center mb-6 bg-gray-100"
          />
  
            {!isSubmitted && slots.length > 0 && (
            <>
              <h3 className="text-md font-semibold mb-2">Available Slots</h3>
              <div className="flex items-center justify-center mb-3">
                <span className={`text-xs font-semibold px-2 py-1 rounded-l ${timezone === 'America/New_York' ? 'bg-blue-900 text-white' : 'bg-gray-200 text-gray-600 cursor-pointer'}`}
                  onClick={() => setTimezone('America/New_York')}
                >
                  ET
                </span>
                <span className={`text-xs font-semibold px-2 py-1 rounded-r ${timezone === 'America/Los_Angeles' ? 'bg-blue-900 text-white' : 'bg-gray-200 text-gray-600 cursor-pointer'}`}
                  onClick={() => setTimezone('America/Los_Angeles')}
                >
                  PT
                </span>
              </div>
              <ul className="space-y-2 w-full">
                {slots
                  .slice(currentPage * SLOTS_PER_PAGE, (currentPage + 1) * SLOTS_PER_PAGE)
                  .map((slot) => (
                    <li key={slot.id} className="border p-2 rounded flex justify-between items-center">
                      <span className="mr-4">
                        {moment.tz(`${slot.date}T${slot.startTime}`, 'America/New_York').tz(timezone).format('ddd MMM D, h:mm A')} {timezoneLabel}
                      </span>
                      <button
                        onClick={() => handleBook(slot)}
                        disabled={bookingSlotId === slot.id || loading || isSubmitted}
                        className="bg-blue-900 text-white text-xs font-semibold px-3 py-1 rounded hover:bg-blue-800 disabled:opacity-50"
                      >
                        {bookingSlotId === slot.id ? 'Booked' : 'Schedule'}
                      </button>
                    </li>
                  ))}
              </ul>
              <div className="flex justify-between w-full mt-4">
                <button
                  onClick={handlePreviousPage}
                  disabled={currentPage === 0}
                  className="bg-gray-100 text-gray-700 text-sm font-medium px-4 py-2 rounded hover:bg-gray-200 transition-colors disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  onClick={handleNextPage}
                  disabled={(currentPage + 1) * SLOTS_PER_PAGE >= slots.length}
                  className="bg-gray-100 text-gray-700 text-sm font-medium px-4 py-2 rounded hover:bg-gray-200 transition-colors disabled:opacity-50"
                >
                  Next
                </button>
              </div>
              <button
                onClick={handleRequestDifferentTime}
                disabled={loading || isSubmitted}
                className="mt-3 w-full text-blue-900 text-sm font-medium py-2 rounded border border-blue-900 hover:bg-blue-50 transition-colors disabled:opacity-50"
              >
                Request a different time
              </button>
            </>
          )}

  
          {confirmation && <div className="mt-4 text-green-700">{confirmation}</div>}
          {error && <div className="mt-4 text-red-700">{error}</div>}
        </div>
      </div>
      {loading && (
        <div className="absolute inset-0 bg-white bg-opacity-80 flex flex-col items-center justify-center rounded-xl z-10">
          <svg
            className="animate-spin h-8 w-8 text-blue-600 mb-3"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            ></circle>
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8v4l3.5-3.5L12 0v4a8 8 0 100 16v-4l3.5 3.5L12 24v-4a8 8 0 01-8-8z"
            ></path>
          </svg>
          <p className="text-blue-700 font-medium">Processing appointment...</p>
        </div>
      )}      
    </div>
  );
};

export default PublicBookingPage;
